/**
 * §A.2 · Lo contratado por un club: verlo, darle plan, cambiarlo y la baja.
 *
 * Hasta el 27-09-2026 esta parte de la ficha solo enseñaba: dar de alta una
 * suscripción o cambiar de plan existía en la API y no tenía pantalla, así que se
 * hacía a mano en la base de datos. Y desde §A.2.1 el plan **decide** —qué
 * funciones tiene el club y cuánta gracia le queda—, así que tenerlo a mano es
 * parte del trabajo diario, no un extra.
 *
 * ## El precio es del club, no del plan
 *
 * Al elegir un plan se propone su precio, y se puede cambiar: es lo que permite el
 * precio fundador o un descuento pactado sin inventarse un plan por cliente.
 * Cambiar de plan **no** cambia el precio si no se toca el campo, porque subirle la
 * cuota a alguien por moverle de plan tiene que ser una decisión, no un efecto.
 *
 * ## La baja no borra nada
 *
 * `DELETE /subscription` la cancela y marca al club como baja. La casilla de «baja
 * administrativa» es para cuando no es una baja de cliente —un cambio de razón
 * social, un club que pasa a pagar desde otra cuenta—, donde contarla falsearía las
 * bajas del mes.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import { aCentimos, aEuros } from '@/club/cuotas/tipos';
import type { Plan, VistaDeSuscripcion } from '@/backoffice/tipos';

type Periodicidad = 'monthly' | 'quarterly' | 'annual';

const PERIODICIDADES: readonly { valor: Periodicidad; etiqueta: string }[] = [
  { valor: 'monthly', etiqueta: 'Mensual' },
  { valor: 'quarterly', etiqueta: 'Trimestral' },
  { valor: 'annual', etiqueta: 'Anual' },
];

const cadaCuanto = (periodicidad: string): string =>
  periodicidad === 'annual'
    ? ' al año'
    : periodicidad === 'quarterly'
      ? ' al trimestre'
      : ' al mes';

type Modo = 'ver' | 'dar' | 'cambiar' | 'baja';

interface Borrador {
  plan: string;
  precio: string;
  periodicidad: Periodicidad;
  diasDePrueba: string;
}

export function SuscripcionDelClub({ clubId }: { clubId: string }) {
  const cache = useQueryClient();
  const [modo, setModo] = useState<Modo>('ver');
  const [borrador, setBorrador] = useState<Borrador>({
    plan: '',
    precio: '',
    periodicidad: 'monthly',
    diasDePrueba: '',
  });
  const [gracia, setGracia] = useState<string | null>(null);
  const [administrativa, setAdministrativa] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const suscripcion = useQuery({
    queryKey: ['admin', 'suscripcion', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .subscription.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as VistaDeSuscripcion;
    },
  });

  // Con los privados: el Fundador no se ofrece, pero se le da a mano a quien toca.
  const planes = useQuery({
    queryKey: ['admin', 'planes'],
    enabled: modo === 'dar' || modo === 'cambiar',
    queryFn: async () => {
      const { data, error } = await api.v1.admin.plans.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Plan[];
    },
  });

  /** Todo lo que cambia con la suscripción: la ficha, los cobros, los avisos. */
  const refrescar = () => {
    for (const clave of ['suscripcion', 'pagos', 'club']) {
      void cache.invalidateQueries({ queryKey: ['admin', clave, clubId] });
    }
    void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
    void cache.invalidateQueries({ queryKey: ['admin', 'avisos-de-planes'] });
  };

  const alAcabar = {
    onSuccess: () => {
      setModo('ver');
      setGracia(null);
      setProblema(null);
      refrescar();
    },
    onError: (e: unknown) => setProblema(aProblema(e)),
  };

  const precioTecleado = (): number | null | undefined => {
    if (borrador.precio.trim() === '') return null;
    return aCentimos(borrador.precio) ?? undefined;
  };

  const mal = (detalle: string) =>
    new ErrorDeApi({
      title: 'Hay algo que no se entiende',
      status: 400,
      detail: detalle,
    });

  const dar = useMutation({
    mutationFn: async () => {
      const precio = precioTecleado();
      const dias = borrador.diasDePrueba.trim();
      if (precio === undefined) throw mal('El precio va en euros: 39 o 39,50.');
      const r = await api.v1.admin.academies({ id: clubId }).subscription.post({
        plan_code: borrador.plan,
        price_cents: precio,
        billing_period: borrador.periodicidad,
        trial_days: dias === '' ? null : Number(dias),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    ...alAcabar,
  });

  const cambiar = useMutation({
    mutationFn: async (antes: {
      plan: string;
      precio: number;
      periodo: string;
    }) => {
      const precio = precioTecleado();
      if (precio === undefined) throw mal('El precio va en euros: 39 o 39,50.');
      const r = await api.v1.admin
        .academies({ id: clubId })
        .subscription.patch({
          ...(borrador.plan !== antes.plan ? { plan_code: borrador.plan } : {}),
          ...(precio !== null && precio !== antes.precio
            ? { price_cents: precio }
            : {}),
          ...(borrador.periodicidad !== antes.periodo
            ? { billing_period: borrador.periodicidad }
            : {}),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    ...alAcabar,
  });

  const cambiarGracia = useMutation({
    mutationFn: async (dias: number) => {
      const r = await api.v1.admin
        .academies({ id: clubId })
        .subscription.patch({ grace_days: dias });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    ...alAcabar,
  });

  const baja = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin
        .academies({ id: clubId })
        .subscription.delete(undefined, {
          query: administrativa ? { keep_club_active: true } : {},
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    ...alAcabar,
  });

  if (suscripcion.isPending) return <Cargando filas={2} etiqueta="Cargando" />;
  if (suscripcion.isError) {
    return (
      <PanelError
        problema={aProblema(suscripcion.error)}
        reintentar={() => void suscripcion.refetch()}
      />
    );
  }

  const viva = suscripcion.data.active;
  const cancelar = () => {
    setModo('ver');
    setProblema(null);
  };

  // ── Darle plan, o cambiárselo ────────────────────────────────────────────
  if (modo === 'dar' || (modo === 'cambiar' && viva)) {
    const cambiando = modo === 'cambiar' && viva;
    const elegido = planes.data?.find((p) => p.code === borrador.plan);
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          if (cambiando) {
            cambiar.mutate({
              plan: viva.plan_code,
              precio: viva.price_cents,
              periodo: viva.billing_period,
            });
          } else {
            dar.mutate();
          }
        }}
      >
        <h3>{cambiando ? 'Cambiar lo contratado' : 'Darle un plan'}</h3>

        {planes.isPending ? (
          <Cargando filas={1} etiqueta="Cargando los planes" />
        ) : (
          <div className="rejilla">
            <label>
              Plan
              <select
                required
                value={borrador.plan}
                onChange={(e) => {
                  const p = planes.data?.find((x) => x.code === e.target.value);
                  setBorrador({
                    ...borrador,
                    plan: e.target.value,
                    // Al dar plan se propone su precio. Al cambiarlo, el del club
                    // se queda si no se toca: ver la cabecera.
                    ...(!cambiando && p
                      ? { precio: aEuros(p.price_cents) }
                      : {}),
                  });
                }}
              >
                <option value="" disabled>
                  Elige uno
                </option>
                {planes.data?.map((p) => (
                  <option key={p.id} value={p.code}>
                    {p.name} · {euros(p.price_cents)}
                    {p.is_public ? '' : ' · no se ofrece'}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Precio para este club
              <input
                inputMode="decimal"
                placeholder={elegido ? aEuros(elegido.price_cents) : '39,00'}
                value={borrador.precio}
                onChange={(e) =>
                  setBorrador({ ...borrador, precio: e.target.value })
                }
              />
            </label>

            <label>
              Cada cuánto paga
              <select
                value={borrador.periodicidad}
                onChange={(e) =>
                  setBorrador({
                    ...borrador,
                    periodicidad: e.target.value as Periodicidad,
                  })
                }
              >
                {PERIODICIDADES.map((p) => (
                  <option key={p.valor} value={p.valor}>
                    {p.etiqueta}
                  </option>
                ))}
              </select>
            </label>

            {!cambiando && (
              <label>
                Días de prueba
                <input
                  inputMode="numeric"
                  placeholder="Sin prueba"
                  value={borrador.diasDePrueba}
                  onChange={(e) =>
                    setBorrador({ ...borrador, diasDePrueba: e.target.value })
                  }
                />
              </label>
            )}
          </div>
        )}

        <div className="nota">
          {cambiando ? (
            <>
              <strong>
                Cambiar de plan le abre o le cierra funciones al momento.
              </strong>{' '}
              El precio solo cambia si lo tocas: el que paga el club es suyo, no
              del plan.
            </>
          ) : (
            <>
              <strong>Sin días de prueba, empieza a pagar hoy</strong>: hasta
              que anotes su primer cobro con «Pagado hasta», el club estará en
              su gracia y verá el aviso de pago pendiente.
            </>
          )}
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={dar.isPending || cambiar.isPending}>
            {cambiando ? 'Guardar el cambio' : 'Darle el plan'}
          </button>
          <button type="button" className="btn-plano" onClick={cancelar}>
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La baja ──────────────────────────────────────────────────────────────
  if (modo === 'baja' && viva) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          baja.mutate();
        }}
      >
        <h3>Dar de baja la suscripción</h3>
        <p>
          El club deja de tener plan y deja de contar en el MRR. Sus datos no se
          tocan.
        </p>
        <label className="opcion">
          <input
            type="checkbox"
            checked={administrativa}
            onChange={(e) => setAdministrativa(e.target.checked)}
          />
          <span>
            <strong>Es una baja administrativa, no de cliente</strong>
            <em>
              Un cambio de razón social o de cuenta: el club sigue activo y no
              cuenta como baja del mes.
            </em>
          </span>
        </label>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="submit"
            className="btn-peligro"
            disabled={baja.isPending}
          >
            Dar de baja
          </button>
          <button type="button" className="btn-plano" onClick={cancelar}>
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Lo que hay ───────────────────────────────────────────────────────────
  if (!viva) {
    return (
      <div className="titulo-fila">
        <p className="tenue">
          Este club no tiene suscripción viva: no se le aplica ningún plan.
        </p>
        <button
          type="button"
          onClick={() => {
            setBorrador({
              plan: '',
              precio: '',
              periodicidad: 'monthly',
              diasDePrueba: '',
            });
            setModo('dar');
          }}
        >
          Darle un plan
        </button>
      </div>
    );
  }

  return (
    <>
      <dl className="datos">
        <div>
          <dt>Plan</dt>
          <dd>{viva.plan_name ?? viva.plan_code}</dd>
        </div>
        <div>
          <dt>Precio</dt>
          <dd>
            {euros(viva.price_cents)}
            <span className="tenue">{cadaCuanto(viva.billing_period)}</span>
          </dd>
        </div>
        <div>
          <dt>Desde</dt>
          <dd>{fecha(viva.started_on)}</dd>
        </div>
        <div>
          <dt>Prueba hasta</dt>
          <dd>{fecha(viva.trial_ends_on)}</dd>
        </div>
        <div>
          <dt>Días de gracia</dt>
          <dd>
            {gracia === null ? (
              <>
                {viva.grace_days}{' '}
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => setGracia(String(viva.grace_days))}
                >
                  Cambiar
                </button>
              </>
            ) : (
              <form
                className="en-linea"
                onSubmit={(e) => {
                  e.preventDefault();
                  cambiarGracia.mutate(Number(gracia));
                }}
              >
                <input
                  type="number"
                  min={0}
                  max={90}
                  required
                  aria-label="Días de gracia"
                  value={gracia}
                  onChange={(e) => setGracia(e.target.value)}
                />
                <button type="submit" disabled={cambiarGracia.isPending}>
                  Guardar
                </button>
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => setGracia(null)}
                >
                  Cancelar
                </button>
              </form>
            )}
          </dd>
        </div>
      </dl>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            setBorrador({
              plan: viva.plan_code,
              precio: aEuros(viva.price_cents),
              periodicidad: viva.billing_period as Periodicidad,
              diasDePrueba: '',
            });
            setModo('cambiar');
          }}
        >
          Cambiar plan o precio
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            setAdministrativa(false);
            setModo('baja');
          }}
        >
          Dar de baja
        </button>
      </div>
    </>
  );
}
